export interface MentionOption {
  key: string
  description?: string
}

export interface MentionSection {
  id: string
  label: string
  tokenPrefix: string
  options: MentionOption[]
  accentClassName?: string
}

export interface ActiveMention {
  atIndex: number
  rangeEnd: number
  query: string
}

export interface ParsedToken {
  start: number
  end: number
  fullMatch: string
  section: string
  key: string
}

interface TextSegment {
  type: "text"
  content: string
  key: string
}

interface TokenSegment {
  type: "token"
  token: ParsedToken
  key: string
}

type MentionSegment = TextSegment | TokenSegment

const MENTION_TRIGGER = "@"

export const TOKEN_MARKER = "\u2063"

const TOKEN_REGEX = new RegExp(
  `${TOKEN_MARKER}([A-Za-z_][\\w]*)\\.([A-Za-z_][\\w]*)`,
  "g",
)
const TEMPLATE_REGEX = /\{\{\s*([A-Za-z_][\w]*)\.([A-Za-z_][\w]*)\s*\}\}/g

export function storedToDisplay(stored: string): string {
  return stored.replace(
    TEMPLATE_REGEX,
    (_match, section: string, key: string) =>
      `${TOKEN_MARKER}${section}.${key}`,
  )
}

export function displayToStored(display: string): string {
  return display.replace(
    TOKEN_REGEX,
    (_match, section: string, key: string) => `{{${section}.${key}}}`,
  )
}

export function parseTokens(
  text: string,
  sections?: MentionSection[],
): ParsedToken[] {
  const tokens: ParsedToken[] = []
  const regex = new RegExp(TOKEN_REGEX.source, "g")
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (sections) {
      const section = sections.find(
        (candidate) => candidate.tokenPrefix === match![1],
      )
      if (!section) {
        continue
      }
      if (!section.options.some((option) => option.key === match![2])) {
        continue
      }
    }
    tokens.push({
      start: match.index,
      end: match.index + match[0].length,
      fullMatch: match[0],
      section: match[1],
      key: match[2],
    })
  }
  return tokens
}

export function splitIntoSegments(
  text: string,
  tokens: ParsedToken[],
): MentionSegment[] {
  if (tokens.length === 0) {
    return [{ type: "text", content: text, key: "text-0" }]
  }
  const segments: MentionSegment[] = []
  let cursor = 0
  tokens.forEach((token, index) => {
    if (token.start > cursor) {
      segments.push({
        type: "text",
        content: text.slice(cursor, token.start),
        key: `text-${cursor}`,
      })
    }
    segments.push({
      type: "token",
      token,
      key: `token-${token.start}-${index}`,
    })
    cursor = token.end
  })
  if (cursor < text.length) {
    segments.push({
      type: "text",
      content: text.slice(cursor),
      key: `text-${cursor}`,
    })
  }
  return segments
}

export function findActiveMention(
  text: string,
  cursor: number,
): ActiveMention | null {
  const before = text.slice(0, cursor)
  const atIndex = before.lastIndexOf(MENTION_TRIGGER)
  const templateIndex = before.lastIndexOf("{{")
  const markerIndex = before.lastIndexOf(TOKEN_MARKER)

  const latest = Math.max(atIndex, templateIndex, markerIndex)
  if (latest === -1) {
    return null
  }

  let triggerStart: number
  let triggerLength: number
  let normalizeLeadingSpaces = false
  let requireWordBoundary = true

  if (markerIndex === latest) {
    triggerStart = markerIndex
    triggerLength = TOKEN_MARKER.length
    requireWordBoundary = false
  } else if (templateIndex === latest) {
    triggerStart = templateIndex
    triggerLength = 2
    normalizeLeadingSpaces = true
  } else {
    triggerStart = atIndex
    triggerLength = 1
  }

  if (requireWordBoundary) {
    const characterBefore = triggerStart === 0 ? "" : before[triggerStart - 1]
    if (characterBefore && !/\s/.test(characterBefore)) {
      return null
    }
  }

  const rawAfter = before.slice(triggerStart + triggerLength)
  const query = normalizeLeadingSpaces
    ? rawAfter.replace(/^[\t ]+/, "")
    : rawAfter
  if (/\s/.test(query)) {
    return null
  }

  return { atIndex: triggerStart, rangeEnd: cursor, query }
}

export function filterSections(
  sections: MentionSection[],
  query: string,
): MentionSection[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) {
    return sections
  }
  return sections
    .map((section) => ({
      ...section,
      options: section.options.filter((option) =>
        option.key.toLowerCase().includes(normalizedQuery),
      ),
    }))
    .filter((section) => section.options.length > 0)
}
